import {useFormikContext} from 'formik';
import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import Button from '../../../../../../../components/button';
import FormikContextInputField from '../../../../../../../components/formikFileds/FormikContextInputField';
import CustomCheckbox from '../../../../../../../components/mazraeKit/customCheckbox';

import {ILoanRequestRuleResponse} from '../../../../../../../services/hookService/getLoanRequestRule/types';

import {CheckTypeSection} from '../../../components/CheckTypeSection';
import {ConditionalField} from '../../../components/CondiitonalField';
import DigitalCheckSection from '../../../components/DigitalCheckSection';
import DocumentMultiImageUploader from '../../../components/DocumentMultiImageUploader';
import VideoInfoWithBtmSheet from '../../../components/VideoInfoWithBtmSheet';

import {hasDocumentsForJobType} from '../../../utils/docs';

import AddExtraDocsForm from '../../Forms/AddExtraDocs';
import {IBankDocsGeneralForm, IPersonFormData} from '../../LoanDocsPage';

import {getSelfCheckCount} from '../consts/PersonSectionConfigs';
import OneGuarantorForm from '../OneGuarantorForm';

import {getPersianOrdinal} from '../../../../../../../utilities/helper/unitConvertor/getPersianOrdinal';
import {PersonPrefix} from './MultiPersonValidation';

export interface ICheckPriceData {
  ownerChequePrices: number[];
  guarantorChequePrices: number[];
}

interface EditPersonFormProps {
  personPrefix: PersonPrefix;
  onComplete: () => void;
  loanRule?: any;
  loanRequestRules: ILoanRequestRuleResponse;
  loanRequestGuid: string;
}

interface IChequeSlot {
  index: 1 | 2 | 3;

  sayadiField:
    | 'first_sayadi_cheque_id'
    | 'second_sayadi_cheque_id'
    | 'third_sayadi_cheque_id';

  imageField: 'first_check_image' | 'second_check_image' | 'third_check_image';

  digitalField:
    | 'first_digital_cheque_id'
    | 'second_digital_cheque_id'
    | 'third_digital_cheque_id';

  price: number;
}

const SELF_CHEQUE_SLOTS: Omit<IChequeSlot, 'price'>[] = [
  {
    index: 1,
    sayadiField: 'first_sayadi_cheque_id',
    imageField: 'first_check_image',
    digitalField: 'first_digital_cheque_id',
  },

  {
    index: 2,
    sayadiField: 'second_sayadi_cheque_id',
    imageField: 'second_check_image',
    digitalField: 'second_digital_cheque_id',
  },

  {
    index: 3,
    sayadiField: 'third_sayadi_cheque_id',
    imageField: 'third_check_image',
    digitalField: 'third_digital_cheque_id',
  },
];

const getPersianIndex = (n: 1 | 2 | 3) =>
  n === 1 ? 'اول' : n === 2 ? 'دوم' : 'سوم';

const EditPersonForm = ({
  personPrefix,
  onComplete,
  loanRequestRules,
  loanRule,
  loanRequestGuid,
}: EditPersonFormProps) => {
  const {values, setFieldValue, validateField} =
    useFormikContext<IBankDocsGeneralForm>();

  const isGuarantor = personPrefix !== 'self';

  const shouldAddExtraDocs = loanRequestRules?.need_extra_document ?? false;

  const personData = values[personPrefix] as IPersonFormData;

  const loanDocuments = loanRule?.loan_type_rules?.[0]?.loan_documents;

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const activeSlots: IChequeSlot[] = useMemo(() => {
    if (isGuarantor) {
      const price = loanRequestRules?.first_guarantor_cheque_price ?? 0;

      if (price <= 0) return [];

      return [{...SELF_CHEQUE_SLOTS[0], price}];
    }

    return SELF_CHEQUE_SLOTS.map((slot, i) => {
      const prices = [
        loanRequestRules?.owner_first_cheque_price,
        loanRequestRules?.owner_second_cheque_price,
        loanRequestRules?.owner_third_cheque_price,
      ];

      return {
        ...slot,
        price: prices[i] ?? 0,
      };
    }).filter(slot => slot.price > 0);
  }, [isGuarantor, loanRequestRules]);

  const [uploadedImages, setUploadedImages] = useState<
    Record<string, File | null>
  >(() => {
    return Object.fromEntries(
      SELF_CHEQUE_SLOTS.map(slot => {
        const value = personData?.[slot.imageField];

        return [slot.imageField, value instanceof File ? value : null];
      }),
    ) as Record<string, File | null>;
  });

  const [checkRegistered, setCheckRegistered] = useState<
    Record<string, boolean>
  >(() => {
    return Object.fromEntries(
      SELF_CHEQUE_SLOTS.map(slot => [slot.imageField, false]),
    );
  });
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const chequeDocumentGuids = useMemo(() => {
    if (!loanDocuments?.length) {
      return [];
    }

    // SELF documents
    if (personPrefix === 'self') {
      const matchedDocs = loanDocuments
        .filter((doc: any) => {
          const title = doc?.title ?? '';

          return title.startsWith('MAIN-SELF-');
        })
        .sort((a: any, b: any) => {
          const getIndex = (title: string) => {
            return Number(title.split('MAIN-SELF-')[1] ?? 0);
          };

          return getIndex(a.title) - getIndex(b.title);
        });

      return matchedDocs.map((doc: any) => doc.guid);
    }

    // GUARANTOR documents
    const guarantorIndex = personPrefix.split('-')[1];

    const matchedDocs = loanDocuments.filter((doc: any) => {
      const title = (doc?.title ?? '').toLowerCase();

      return title.startsWith(`main-guarantor-${guarantorIndex}`.toLowerCase());
    });
    return matchedDocs.map((doc: any) => doc.guid);
  }, [loanDocuments, personPrefix]);

  const getFieldName = (field: keyof IPersonFormData) =>
    `${personPrefix}.${field}`;

  const setNestedValue = (field: keyof IPersonFormData, value: any) => {
    setFieldValue(getFieldName(field), value);

    validateField(getFieldName(field));
  };

  const areAllExtraImagesUploaded = useCallback((): boolean => {
    if (!personData) return false;

    const extraImageFields = Object.keys(personData).filter(key =>
      key.startsWith('extra_image_'),
    );

    if (extraImageFields.length === 0) {
      return true;
    }

    return extraImageFields.every(key => {
      const value = personData[key as keyof IPersonFormData];

      return (
        value !== null &&
        value !== undefined &&
        value !== '' &&
        (value instanceof File || typeof value === 'string')
      );
    });
  }, [personData]);

  const prevCheckTypeRef = useRef(personData?.checkType);

  useEffect(() => {
    if (
      prevCheckTypeRef.current &&
      prevCheckTypeRef.current !== personData?.checkType
    ) {
      SELF_CHEQUE_SLOTS.forEach(slot => {
        setNestedValue(slot.sayadiField, '');

        setNestedValue(slot.imageField, null);

        setNestedValue(slot.digitalField, '');
      });

      setUploadedImages(
        Object.fromEntries(
          SELF_CHEQUE_SLOTS.map(slot => [slot.imageField, null]),
        ),
      );

      setCheckRegistered(
        Object.fromEntries(
          SELF_CHEQUE_SLOTS.map(slot => [slot.imageField, false]),
        ),
      );
    }

    prevCheckTypeRef.current = personData?.checkType;
  }, [personData?.checkType]);

  const shouldDisableButton = useMemo(() => {
    if (!personData) return true;

    if (isGuarantor && (!personData.name || !personData.cellphone_number)) {
      return true;
    }

    if (!personData.checkType) return true;

    if (personData.checkType === 'digital') {
      const allValid = activeSlots.every(slot => {
        const value = personData[slot.digitalField];

        return typeof value === 'string' && value.length === 16;
      });

      if (!allValid) return true;
    }

    if (personData.checkType === 'paper') {
      const allSayadiFilled = activeSlots.every(slot => {
        const value = personData[slot.sayadiField];

        return typeof value === 'string' && /^\d{16}$/.test(value);
      });

      if (!allSayadiFilled) return true;
    }

    if (
      shouldAddExtraDocs &&
      (!personData.job_type ||
        (hasDocumentsForJobType(
          loanDocuments!,
          personData?.job_type,
          personPrefix,
        ) &&
          !personData?._allJobImagesUploaded &&
          !areAllExtraImagesUploaded()))
    ) {
      return true;
    }

    return false;
  }, [
    personData,
    isGuarantor,
    shouldAddExtraDocs,
    loanRequestRules,
    activeSlots,
  ]);

  const handleSave = () => {
    setIsSaving(true);

    onComplete();

    setIsSaving(false);
  };

  const isAllCheckImagesUploaded = useMemo(() => {
    if (isGuarantor) {
      const firstImage =
        uploadedImages.first_check_image || personData?.first_check_image;

      return Boolean(firstImage);
    }

    return activeSlots.every(slot => {
      const image =
        uploadedImages[slot.imageField] || personData?.[slot.imageField];

      return Boolean(image);
    });
  }, [isGuarantor, uploadedImages, personData, activeSlots]);

  return (
    <div className="flex flex-col w-full h-full pb-4">
      {' '}
      <div className="flex-1 px-3 pb-20 space-y-4 overflow-y-auto">
        {personPrefix !== 'self' && (
          <OneGuarantorForm fieldPrefix={personPrefix} />
        )}

        <ConditionalField show={shouldAddExtraDocs}>
          <AddExtraDocsForm
            fieldPrefix={personPrefix}
            loanDocuments={loanDocuments ?? []}
            loanRequestGuid={loanRequestGuid}
            loanRuleGuid={loanRule?.loan_type_rules?.[0]?.guid ?? ''}
          />
        </ConditionalField>

        <ConditionalField
          show={shouldAddExtraDocs ? Boolean(personData?.job_type) : true}
        >
          <CheckTypeSection fieldPrefix={personPrefix as PersonPrefix} />
        </ConditionalField>

        {personData?.checkType === 'paper' && (
          <>
            <VideoInfoWithBtmSheet
              title="آموزش پر کردن چک"
              btmTitle="نحوه پر کردن چک"
              videoLink="https://public-videos.s3.ir-thr-at1.arvanstorage.ir/%D9%BE%D8%B1%20%DA%A9%D8%B1%D8%AF%D9%86%20%DA%86%DA%A9.mp4?versionId="
            />

            <div
              ref={scrollContainerRef}
              tabIndex={-1}
              style={{outline: 'none'}}
              className="flex gap-4 mt-0"
            >
              {activeSlots.map((slot, index) => {
                const label =
                  activeSlots.length > 1
                    ? `چک ${getPersianIndex(slot.index)}`
                    : 'چک';

                return (
                  <div key={slot.imageField} className="mb-4">
                    <DocumentMultiImageUploader
                      title={`بارگذاری تصویر ${label}`}
                      loanRequestGuid={loanRequestGuid}
                      loanRuleGuid={loanRule?.loan_type_rules?.[0]?.guid ?? ''}
                      loanDocumentGuid={chequeDocumentGuids[index]}
                      personPrefix={personPrefix}
                      fieldName={slot.imageField}
                      onImageUploaded={file => {
                        setUploadedImages(prev => ({
                          ...prev,
                          [slot.imageField]: file,
                        }));

                        setNestedValue(slot.imageField, file);
                      }}
                    />
                  </div>
                );
              })}
            </div>

            <ConditionalField show={!!isAllCheckImagesUploaded}>
              <div className="mt-4 space-y-4">
                <VideoInfoWithBtmSheet
                  title="آموزش ثبت چک در سامانه صیاد"
                  btmTitle="نحوه ثبت چک در سامانه صیاد"
                  videoLink="https://public-videos.s3.ir-thr-at1.arvanstorage.ir/%D8%AB%D8%A8%D8%AA%20%DA%86%DA%A9%20.mp4?versionId="
                />

                <CustomCheckbox
                  name={`${personPrefix}.allChecksRegistered`}
                  customClass="gap-1.5"
                  title="تمام چک ها را در سامانه صیاد ثبت کردم"
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    const checked = e.target.checked;

                    setCheckRegistered(
                      Object.fromEntries(
                        activeSlots.map(slot => [slot.imageField, checked]),
                      ),
                    );
                  }}
                  checked={activeSlots.every(
                    slot => checkRegistered[slot.imageField],
                  )}
                />
              </div>
            </ConditionalField>

            <ConditionalField
              show={activeSlots.every(slot => checkRegistered[slot.imageField])}
            >
              <div className="flex flex-col gap-0.5">
                <span className="mt-2 text-sm font-normal text-gray-700">
                  {getSelfCheckCount(loanRequestRules) > 1
                    ? 'شناسه صیاد چک ها'
                    : 'شناسه صیاد چک '}
                  <span className="text-red-600">*</span>
                </span>
                {activeSlots.map((slot, index) => {
                  return (
                    <FormikContextInputField
                      key={slot.sayadiField}
                      name={getFieldName(slot.sayadiField)}
                      label={``}
                      placeholder={`شناسه صیادی چک ${
                        getPersianOrdinal(index + 1) ?? index + 1
                      } را وارد کنید`}
                      required
                      showRequiredStar={false}
                      type="number"
                      inputMode="numeric"
                      maxLength={16}
                    />
                  );
                })}
              </div>
            </ConditionalField>
          </>
        )}

        {personData?.checkType === 'digital' && (
          <ConditionalField show={true}>
            <div className="space-y-4">
              <DigitalCheckSection
                checkPriceData={{
                  ownerChequePrices: [
                    loanRequestRules?.owner_first_cheque_price,
                    loanRequestRules?.owner_second_cheque_price,
                    loanRequestRules?.owner_third_cheque_price,
                  ].filter(Boolean) as number[],

                  guarantorChequePrices: [
                    loanRequestRules?.first_guarantor_cheque_price,
                    loanRequestRules?.second_guarantor_cheque_price,
                  ].filter(Boolean) as number[],
                }}
                checkDate={loanRequestRules?.cheque_receipt_date}
                receiverName={loanRequestRules?.receiver_name}
                MeliCardId={loanRequestRules?.national_id}
                selfCheckCounts={getSelfCheckCount(loanRequestRules)}
                fieldPrefix={personPrefix}
              />
            </div>
          </ConditionalField>
        )}
      </div>
      <div className="fixed bottom-0 left-0 right-0 z-50 w-full flex flex-col gap-2 p-3 bg-white shadow-[0_-1px_6px_0_rgba(0,0,0,0.1)]">
        <Button
          type="button"
          onClick={handleSave}
          disabled={shouldDisableButton || isSaving}
          isLoading={isSaving}
        >
          {isGuarantor ? 'ثبت ضامن' : 'تایید'}
        </Button>
      </div>
    </div>
  );
};

export default EditPersonForm;
